import Button from 'components/Buttons/Button/Button';
import { usePlayer } from 'providers/player/PlayerContext';
import React, { FC, useEffect, useId, useRef, useState } from 'react';
import { CardImage } from 'style/styled-components/reusable.css';
import { getLocalStorage } from 'utils/fnStorage';
import translate from 'utils/fnTranslate';
import useYouboraContentTracking from 'hooks/useYouboraContentTracking';
import fallbackLandscape from 'assets/images/landscape_fallback.png';
import { setLastCompletedEpisode } from 'components/Pages/PageVODSeriesDetail/PageVODSeriesDetail';
import { BingeWatchingContainer, BingeWatchingCover, BingeWatchingDetails, Buttons, Description, Title } from './BingeWatchingBanner.css';

type props = {
    id: string;
    seriesId?: string;
    title: string;
    timeLeft: number;
    episode_number: number;
    season_number: number;
    episode_title: string;
    image: string;
    loadNextEpisode: () => void;
    dismissBanner: (arg?: boolean) => void;
    showLimitNotification: boolean;
};

export const BingeWatchingBanner: FC<props> = ({
    id,
    seriesId,
    title,
    episode_number,
    season_number,
    episode_title,
    image,
    showLimitNotification = false,
    loadNextEpisode,
    dismissBanner,
}) => {
    const { setDisplayBingeWatch, setShowNextEpisode, bingeWatchDuration, getProgressWithJump, getPostPadding, getDuration } = usePlayer();
    const { trackPlayerInteraction } = useYouboraContentTracking();
    const allowAutoPlay = getLocalStorage('autoPlay');
    const DEFAULT_AUTOPLAY = allowAutoPlay === null;
    const showAutoPlay = allowAutoPlay || DEFAULT_AUTOPLAY;

    const elapsedTime = getProgressWithJump();
    const duration = getDuration();
    const remainingTimeInSeconds = duration - elapsedTime - getPostPadding();

    const [timeOut, setTimeOut] = useState(bingeWatchDuration);
    const updateTimerRef = useRef(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const descriptionId = useId();
    const countdownId = useId();
    const [announcement, setAnnouncement] = useState('');
    const showCountdown = showAutoPlay && !showLimitNotification;
    const countdownText = showCountdown ? translate('BINGE_WATCHING_HINT_FALLBACK', { count: bingeWatchDuration }) : '';

    useEffect(() => {
        if (!showAutoPlay || showLimitNotification) {
            return;
        }

        if (updateTimerRef.current) {
            clearInterval(updateTimerRef.current);
        }

        updateTimerRef.current = setInterval(() => {
            if (remainingTimeInSeconds < 5 || timeOut <= 1) {
                setTimeOut(0);
                clearInterval(updateTimerRef.current);
                trackPlayerInteraction('Auto Next Episode');
                loadNextEpisode();
                return;
            }
            setTimeOut((prevTimeout) => prevTimeout - 1);
        }, 1000);
    }, [timeOut, showAutoPlay, showLimitNotification, loadNextEpisode, trackPlayerInteraction]);

    const handleNextEpisode = () => {
        trackPlayerInteraction('Manual Next Episode', id);
        setTimeOut(0);
        if (updateTimerRef.current) {
            clearInterval(updateTimerRef.current);
        }
        loadNextEpisode();
    };

    const handleDismissBanner = (isLimit?: boolean) => {
        trackPlayerInteraction(isLimit ? 'Stop Binge Watching' : 'Complete Episode', id);

        // Save the last completed episode to localStorage when user clicks "Complete Episode"
        if (!isLimit && seriesId && id) {
            setLastCompletedEpisode(seriesId, id);
        }

        dismissBanner(isLimit);
    };

    useEffect(
        () => () => {
            setDisplayBingeWatch(false);
            clearInterval(updateTimerRef.current);
        },
        [setDisplayBingeWatch]
    );

    useEffect(() => {
        if (!showAutoPlay) {
            setShowNextEpisode(true);
        }
    }, [showAutoPlay, setShowNextEpisode]);

    const getData = () => {
        let bannerTitle: string;
        let bannerSubtitle: string;

        if (showLimitNotification) {
            bannerTitle = translate('BINGE_WATCHING_LIMIT_TITLE');
            bannerSubtitle = translate('BINGE_WATCHING_LIMIT_HINT');
        } else {
            bannerTitle = title;
            bannerSubtitle = `
                ${season_number ? translate('HOVER_SEASON') + season_number : ''}
                ${episode_number ? translate('HOVER_EPISODE') + episode_number : ''}
                ${episode_title || ''}
            `;
        }

        return {
            bannerTitle,
            bannerSubtitle,
        };
    };

    const getButtons = () => (
        <>
            <Button
                label={translate(showLimitNotification ? 'STOP_BUTTON' : 'COMPLETE_EPISODE_BUTTON')}
                onClick={() => handleDismissBanner(showLimitNotification)}
            />
            <Button
                label={translate(showLimitNotification ? 'CONTINUE_BUTTON' : 'NEXT_EPISODE_BUTTON')}
                progressAnimationDuration={showAutoPlay ? bingeWatchDuration : 0}
                onClick={() => handleNextEpisode()}
                isPrimary
            />
        </>
    );

    const { bannerTitle, bannerSubtitle } = getData();

    useEffect(() => {
        if (!document.activeElement || document.activeElement === document.body) {
            containerRef.current?.focus();
            return;
        }

        setAnnouncement(
            [bannerTitle, showLimitNotification ? bannerSubtitle : countdownText]
                .filter(Boolean)
                .map((part) => (/[.?!]$/.test(part.trim()) ? part.trim() : `${part.trim()}.`))
                .join(' ')
        );
    }, []);

    if (timeOut === 0) return null;

    return (
        <BingeWatchingContainer
            ref={containerRef}
            role="group"
            tabIndex={-1}
            aria-labelledby={titleId}
            aria-describedby={showCountdown ? `${descriptionId} ${countdownId}` : descriptionId}
        >
            <span className="sr-only" role="status">
                {announcement}
            </span>
            <BingeWatchingDetails>
                <Title id={titleId}>{bannerTitle}</Title>
                <Description id={descriptionId}>{bannerSubtitle}</Description>
                {showCountdown && (
                    <span id={countdownId} className="sr-only">
                        {countdownText}
                    </span>
                )}
                <Buttons>{getButtons()}</Buttons>
            </BingeWatchingDetails>
            <BingeWatchingCover>
                <CardImage url={image} fallbackImage={fallbackLandscape} />
            </BingeWatchingCover>
        </BingeWatchingContainer>
    );
};
